import { useEffect, useRef } from "react";
import type { RefObject } from "react";

/** What a Tab can land on inside a dialog: enabled controls and explicit tab stops (not `tabindex="-1"`). */
const TABBABLE =
  'button:not(:disabled), input:not(:disabled):not([type="hidden"]), select:not(:disabled), textarea:not(:disabled), [tabindex]:not([tabindex="-1"])';
/** Where focus lands on open when the caller names nothing: the first enabled field or button, in document order. */
const FIRST_FOCUS = 'input:not(:disabled):not([type="hidden"]), select:not(:disabled), textarea:not(:disabled), button:not(:disabled)';

// Mount order is the modal stack. Covered dialogs must not intercept the top one's keys.
const dialogStack: HTMLElement[] = [];

export interface DialogTrapOptions {
  /** True while a save is in flight: Escape does nothing (the dialog stays up to show the outcome). */
  busy?: boolean;
  /** Focus this on open instead of the first field (a confirm focuses its safe button, a detail view its Close). */
  initialFocus?: RefObject<HTMLElement | null>;
  /** Give focus back to whatever had it when the dialog opened, once it unmounts. */
  restoreFocus?: boolean;
}

/**
 * Focus-in, Escape-to-close and a Tab trap for a modal. The ONE implementation behind `DialogShell` — the app used to carry
 * twenty copies of this effect, each with a slightly different focusable selector. Mount-only: `onClose` / `busy` are read
 * through refs, so a parent re-render never re-runs the focus step or steals focus back from a field.
 */
export function useDialogTrap(
  dialogRef: RefObject<HTMLElement | null>,
  onClose: () => void,
  { busy = false, initialFocus, restoreFocus = false }: DialogTrapOptions = {},
): void {
  const closeRef = useRef(onClose);
  const busyRef = useRef(busy);
  closeRef.current = onClose;
  busyRef.current = busy;
  const initialRef = useRef(initialFocus);
  initialRef.current = initialFocus;

  useEffect(() => {
    const opener = document.activeElement as HTMLElement | null;
    const dialog = dialogRef.current;
    if (dialog) dialogStack.push(dialog);
    (initialRef.current?.current ?? dialog?.querySelector<HTMLElement>(FIRST_FOCUS))?.focus();

    function onKeyDown(e: KeyboardEvent): void {
      if (!dialog || dialogStack[dialogStack.length - 1] !== dialog) return;
      if (e.key === "Escape") {
        if (!busyRef.current) closeRef.current();
        return;
      }
      if (e.key !== "Tab") return;
      const root = dialogRef.current;
      const tabbable = root?.querySelectorAll<HTMLElement>(TABBABLE);
      if (!root || !tabbable || tabbable.length === 0) return;
      const first = tabbable[0];
      const last = tabbable[tabbable.length - 1];
      const active = document.activeElement;
      // Focus that has left the dialog (or sits on a button that just became disabled) comes back in.
      if (!active || !root.contains(active) || (active instanceof HTMLButtonElement && active.disabled)) {
        e.preventDefault();
        (e.shiftKey ? last : first)?.focus();
      } else if (e.shiftKey && active === first) {
        e.preventDefault();
        last?.focus();
      } else if (!e.shiftKey && active === last) {
        e.preventDefault();
        first?.focus();
      }
    }
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("keydown", onKeyDown);
      const wasTop = dialogStack[dialogStack.length - 1] === dialog;
      const index = dialog ? dialogStack.lastIndexOf(dialog) : -1;
      if (index !== -1) dialogStack.splice(index, 1);
      if (restoreFocus && wasTop && opener?.isConnected) opener.focus();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- mount-only by design: everything it reads comes through refs
  }, []);
}
